import { StaticProvider } from '@angular/core';

import { AppSession, toAppSession } from './app/interfaces/app-session';
import {
  INITIAL_SESSION,
  SESSION_UNAVAILABLE
} from './app/services/auth.service';
import { SessionDto } from './app/shared/vive-api/vive-api-models';

/**
 * The pre-bootstrap session load and the bootstrap ordering. Kept out of
 * main.ts so the branching is unit-testable without the page-load side effects
 * (analytics init, window.location, the real network).
 */

const SESSION_FETCH_TIMEOUT_MS = 8000;
/** The waits before the second and third attempts. */
const SESSION_RETRY_DELAYS_MS: readonly number[] = [3000, 7000];
/** The whole load, from the first attempt, never holds the page longer than this. */
const SESSION_LOAD_BUDGET_MS = 15000;

/** The Datadog RUM action sent when the session load gives up; a monitor counts it. */
export const SESSION_BOOTSTRAP_DEGRADED_ACTION = 'session_bootstrap_degraded';

/** The `vendor-app-redirect` rollout inputs. */
export interface RedirectFlag {
  flagEnabled: boolean;
  payload: string[] | null;
}

export interface SessionLoadOptions {
  apiUrl: string;
  /** Called once, with each attempt's failure, when no attempt settled the session. A throw is swallowed. */
  onDegraded: (reasons: string[]) => void;
  fetchFn?: typeof fetch;
  timeoutMs?: number;
  retryDelaysMs?: readonly number[];
  budgetMs?: number;
}

/**
 * What the load concluded. `unavailable` means the API never answered with a
 * session or a 401, so whether anyone is signed in is still unknown.
 */
export type SessionLoadResult =
  | { kind: 'signed-in'; session: AppSession }
  | { kind: 'anonymous' }
  | { kind: 'unavailable' };

export interface StartAppOptions {
  sessionReady: Promise<SessionLoadResult>;
  /** False when the kill switch is on, no vendors URL is set, or a Connect exchange is pending. */
  redirectsEnabled: boolean;
  waitForRedirectFlag: () => Promise<RedirectFlag | null>;
  resolveRedirectUrl: (
    flag: RedirectFlag,
    session: AppSession | null
  ) => Promise<string | null>;
  redirect: (url: string) => void;
  bootstrap: (result: SessionLoadResult) => void;
}

type SessionFetchResult =
  | { kind: 'signed-in'; session: AppSession }
  | { kind: 'anonymous' }
  | { kind: 'failed'; reason: string };

/**
 * The pre-session app kept its bearer JWT under these localStorage keys. A copy
 * left by an earlier bundle stays valid until it expires and JavaScript can read
 * it, so every load removes it.
 */
export function purgeRetiredCredentialKeys(
  storage: Pick<Storage, 'removeItem'>
): void {
  // eslint-disable-next-line no-restricted-syntax -- the purge is the one sanctioned reference to the retired keys
  storage.removeItem('userInfo');
  // eslint-disable-next-line no-restricted-syntax -- the purge is the one sanctioned reference to the retired keys
  storage.removeItem('storedUserInfo');
}

/**
 * One `GET /api/v3/auth/session`. The API host is cross-origin, so the session
 * cookie rides only with `credentials: 'include'`.
 */
async function fetchSessionOnce(
  apiUrl: string,
  fetchFn: typeof fetch,
  timeoutMs: number
): Promise<SessionFetchResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchFn(`${apiUrl}/api/v3/auth/session`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: controller.signal
    });
    if (response.status === 401) {
      return { kind: 'anonymous' };
    }
    if (!response.ok) {
      return { kind: 'failed', reason: `HTTP ${response.status}` };
    }
    return {
      kind: 'signed-in',
      session: toAppSession((await response.json()) as SessionDto)
    };
  } catch {
    return {
      kind: 'failed',
      reason: controller.signal.aborted ? 'timeout' : 'network'
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Who is signed in, settled before anything renders. 401 is the only answer
 * that means anonymous. Anything else that is not a session — a network
 * error, the abort, any other status — means the API could not answer, so it
 * is retried with backoff inside one budget: the cookie is usually still
 * valid and a load balancer recovers within seconds. A wait that would end
 * past the budget is not started; the load gives up at once instead. It never
 * rejects, so startApp() can rely on the session always settling.
 */
export async function loadSession({
  apiUrl,
  onDegraded,
  // Wrapped so `fetch` is never called detached from `window`.
  fetchFn = (input, init) => fetch(input, init),
  timeoutMs = SESSION_FETCH_TIMEOUT_MS,
  retryDelaysMs = SESSION_RETRY_DELAYS_MS,
  budgetMs = SESSION_LOAD_BUDGET_MS
}: SessionLoadOptions): Promise<SessionLoadResult> {
  const deadline = Date.now() + budgetMs;
  const reasons: string[] = [];
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    if (attempt > 0) {
      const delay = retryDelaysMs[attempt - 1];
      if (Date.now() + delay >= deadline) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    const result = await fetchSessionOnce(
      apiUrl,
      fetchFn,
      Math.min(timeoutMs, deadline - Date.now())
    );
    if (result.kind !== 'failed') {
      return result;
    }
    reasons.push(result.reason);
  }
  try {
    onDegraded(reasons);
  } catch {
    // The report is telemetry. A throw here must not stop the app starting.
  }
  return { kind: 'unavailable' };
}

/** The platform providers that hand the settled load to `AuthService`. */
export function bootProviders(result: SessionLoadResult): StaticProvider[] {
  return [
    {
      provide: INITIAL_SESSION,
      useValue: result.kind === 'signed-in' ? result.session : null
    },
    { provide: SESSION_UNAVAILABLE, useValue: result.kind === 'unavailable' }
  ];
}

/**
 * The redirect inputs once PostHog reports its flags, or null if it has not
 * within `timeoutMs`. This bound applies to the flag wait and nothing else:
 * startApp() still waits for the session.
 */
export function waitForFlagWithin(
  onFlagsReady: (callback: () => void) => void,
  readFlag: () => RedirectFlag,
  timeoutMs: number
): Promise<RedirectFlag | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    onFlagsReady(() => {
      clearTimeout(timer);
      resolve(readFlag());
    });
  });
}

/**
 * Bootstrap, or first hand a cut-over vendor to vive-vendors. Bootstrap always
 * waits for the session: the synchronous AuthService getters, the route guards
 * and the identity calls all read it, and a guard that ran first would send a
 * signed-in user to /login.
 *
 * Once the session has settled, a failing flag wait, redirect decision or
 * redirect falls through to bootstrap: no timer is left to start the app. The
 * catch sits after the session await on purpose, because one that could fire
 * sooner would bootstrap ahead of the session.
 */
export async function startApp({
  sessionReady,
  redirectsEnabled,
  waitForRedirectFlag,
  resolveRedirectUrl,
  redirect,
  bootstrap
}: StartAppOptions): Promise<void> {
  // Started now, so the flag wait runs beside the session load and not after
  // it. A failure here means "no flag", so it cannot settle ahead of the session.
  const flagReady: Promise<RedirectFlag | null> = redirectsEnabled
    ? Promise.resolve()
        .then(() => waitForRedirectFlag())
        .catch(() => null)
    : Promise.resolve(null);

  const result = await sessionReady;

  try {
    const flag = await flagReady;
    const session = result.kind === 'signed-in' ? result.session : null;
    const redirectUrl = flag ? await resolveRedirectUrl(flag, session) : null;
    if (redirectUrl) {
      redirect(redirectUrl);
      return; // the redirect takes over the page
    }
  } catch {
    // Fall through: start the app rather than leave the page blank.
  }
  bootstrap(result);
}
